import { useMutation } from "@apollo/client"
import { yupResolver } from "@hookform/resolvers/yup"
import { camelCase } from "lodash-es"
import React, { useCallback, useState, useRef } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import ClientData from "@/common/client_data"
import FormCheckbox from "@/common/components/form_checkbox"
import FormForm from "@/common/components/form_form"
import FormInput from "@/common/components/form_input"
import FormTextArea from "@/common/components/form_textarea"
import env from "@/common/env"
import yup from "@/common/yup"
import { InfoFill, PaperPlaneOutline } from "@/skylticons"
import Uploader from "@/uploader/components/uploader"

const messageDoc = graphql(`
  mutation MessageCreate($email: String!, $question: String!, $name: String, $files: [JSON!]) {
    messageCreate(email: $email, question: $question, name: $name, files: $files) {
      success

      errors {
        path
        message
      }
    }
  }
`)

const emailSnippet = ClientData.snippet("contact_us_email")
const phoneSnippet = ClientData.snippet("contact_us_phone")
const phoneTimesSnippet = ClientData.snippet("contact_us_phone_times")

interface ContactUsProps {
  scrollUp: () => void
}

export interface MessageInputs {
  [key: string]: string | string[] | undefined

  email: string
  question: string
  name?: string
  image?: string[]
}

const contactSchema = yup.object().shape<MessageInputs>({
  email: yup.string().required(),
  question: yup.string().required(),
  name: yup.string(),
  image: yup.array().of(yup.string()),
})

const ContactUs: React.FC<ContactUsProps> = ({ scrollUp }) => {
  const { t } = useTranslation()
  const [showUploader, setShowUploader] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [token, setToken] = useState(false)
  const recaptchaWrapperRef = useRef<any>()
  const recaptchaRef = useRef<any>()
  const submitRef = useRef<HTMLButtonElement>(null)

  const toggleUploader = useCallback(() => {
    setShowUploader(v => !v)
  }, [])

  const formMethods = useForm<MessageInputs>({
    resolver: yupResolver(contactSchema),
  })

  const { register, handleSubmit, formState, setError } = formMethods

  const [mutation] = useMutation(messageDoc)

  const loading = formState.isSubmitting

  const submit: SubmitHandler<MessageInputs> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      if (!token && !env.TEST) {
        recaptchaWrapperRef.current.classList.remove("hidden")
        return
      }

      const files: { [key: string]: any } = []
      form.image?.forEach(img => {
        files.push(JSON.parse(img))
      })

      const result = await mutation({
        variables: { ...form, files },
      })
      const data = result.data?.messageCreate

      if (data) {
        const errors = data.errors

        if (errors && errors.length > 0) {
          for (const error of errors) {
            if (error.path && error.path.length > 0) {
              setError(camelCase(error.path[0]), { type: "validate", message: error.message })
            }
          }

          return
        }

        if (data.success) {
          setSubmitted(true)
          scrollUp()
        }
      }
    },
    [mutation, token, setError, scrollUp]
  )

  const onRecaptchaChange = useCallback(() => {
    setToken(recaptchaRef.current.getValue())
    if (submitRef.current) {
      submitRef.current.click()
    }
  }, [])

  return (
    <>
      <div className="contact-col contact-details">
        <div className="wrapper">
          <p className="bold mb-0" dangerouslySetInnerHTML={{ __html: emailSnippet }}></p>

          {phoneSnippet && (
            <>
              <p className="bold phone" dangerouslySetInnerHTML={{ __html: phoneSnippet }}></p>
              <div className="paragraph-sm mb-0" dangerouslySetInnerHTML={{ __html: phoneTimesSnippet }}></div>
            </>
          )}
        </div>
      </div>
      {!submitted && (
        <div className="contact-col contact-form">
          <div className="wrapper">
            <p className="bold">{t("pages.use_this_form")}</p>
            <FormForm
              id="widget-contact-us"
              className="new_message"
              noValidate
              onSubmit={handleSubmit(submit)}
              formMethods={formMethods}
            >
              <FormInput name="name" id="message_name" label={t("address.name")} />

              <FormInput
                name="email"
                id="message_email"
                label={`* ${t("help_center.e_mail")}`}
                type="email"
                placeholder={t("pages.email_used_for")}
                rules={{ required: true }}
              />

              <FormTextArea
                name="question"
                id="message_question"
                rows={3}
                label={`* ${t("help_center.question")}`}
                placeholder={t("help_center.question_placeholder")}
                rules={{ required: true }}
              />

              <FormCheckbox
                name="upload_image"
                id="message_upload_image"
                label={t("pages.yes_upload_image")}
                onChange={() => toggleUploader()}
                checked={showUploader}
              />

              {showUploader && (
                <div className="form-group">
                  <p className="paragraph-sm mb-0">{t("pages.upload_image_as_reference")}</p>
                  <Uploader name="image" formRegister={register} />
                </div>
              )}

              <button
                id="message-submit"
                className="button button-primary full-width icon-right"
                type="submit"
                disabled={loading}
                ref={submitRef}
              >
                {t("help_center.send_question")}
                <PaperPlaneOutline />
              </button>

              <div className="recaptcha hidden" ref={recaptchaWrapperRef}>
                {!env.TEST && (
                  <ReCAPTCHA ref={recaptchaRef} sitekey={ClientData.recaptchaKey || ""} onChange={onRecaptchaChange} />
                )}
              </div>
            </FormForm>
            <div className="ordernr-info">
              <InfoFill />
              <span className="paragraph-xs mb-0">{t("messages.ordernr")}</span>
            </div>
            <div className="notice_snippet" dangerouslySetInnerHTML={{ __html: ClientData.snippet("contact") }}></div>
          </div>
        </div>
      )}

      {submitted && (
        <div className="contact-col">
          <div className="wrapper">
            <h3>{t("help_center.question_sent")}</h3>
            <p>{t("help_center.back_to_you")}</p>
          </div>
        </div>
      )}
      <div className="contact-col legal" dangerouslySetInnerHTML={{ __html: ClientData.snippet("legal") }}></div>
    </>
  )
}

export default ContactUs
